<script>
  import { faArrowLeft, faArrowRight, faUser, faUsers } from "@fortawesome/pro-regular-svg-icons"
  import { _ } from "../stores/index"
  import { Flex, FlexItem, TextField, Button, Icon, Checkbox } from "@gl/design-system"
  import { getContext } from "svelte"
  const { isAuthorized } = getContext("isAuthorized")

  export let data
  export let disabled
  export let checkboxes = true
  export let resource = "members"

  const initialData = { ...data }

  let search = { current: "", available: "" }
  let searched = {}
  let selected = {
    current: [],
    available: [],
  }

  let display = {
    user: true,
    group: true,
  }

  const handleAssignmentClick = (table, resource) => {
    if (selected[table].some((r) => resource.id === r.id)) {
      selected[table] = selected[table].filter((e) => e.id !== resource.id)
    } else {
      selected[table] = [...selected[table], resource]
    }
  }

  const handleAddRemove = (type) => {
    const originalColumn = type === "add" ? "available" : "current"

    const destinationColumn = type === "add" ? "current" : "available"

    if (selected[originalColumn].length) {
      data[destinationColumn] = [...data[destinationColumn], ...selected[originalColumn]]
      data[originalColumn] = data[originalColumn].filter((e) => !selected[originalColumn].some((el) => el.id === e.id))

      if (searched[destinationColumn])
        searched[destinationColumn] = [...searched[destinationColumn], ...selected[originalColumn]]

      if (searched[originalColumn])
        searched[originalColumn] = searched[originalColumn].filter(
          (e) => !selected[originalColumn].some((el) => el.id === e.id)
        )
    }
    selected = {
      current: [],
      available: [],
    }
  }

  const handleSearch = (type) => {
    searched[type] = data[type].filter((m) => m.label.toUpperCase().includes(search[type].toUpperCase()))
  }

  const handleCheckbox = () => {
    for (const type of ["current", "available"]) {
      searched[type] = data[type].filter((el) => {
        return !Object.entries(display).some(([displayType, value]) => {
          return !value && el.type === displayType
        })
      })
    }
  }

  export function reset() {
    data = { ...initialData }
    display.group = true
    display.user = true
  }

  export function getCurrent() {
    const selected = {
      all: data.current.flatMap((m) => m.id),
    }

    for (const c of data.current) {
      if (c.type && selected[c.type]) {
        selected[c.type].push(c.id)
      } else if (c.type) {
        selected[c.type] = [c.id]
      }
    }

    return selected
  }
</script>

{#if checkboxes}
  <Flex class="my-2 align-items-center">
    <FlexItem class="mr-4 mt-2">
      <b>{$_("common.show")}</b>
    </FlexItem>
    {#if $isAuthorized("group_read")}
      <FlexItem class="mr-2">
        <Checkbox bind:value={display.group} label={$_("common.groups")} on:change={handleCheckbox} />
      </FlexItem>
    {/if}
    <FlexItem>
      <Checkbox bind:value={display.user} label={$_("common.users")} on:change={handleCheckbox} />
    </FlexItem>
  </Flex>
{/if}

<div class="grid-container">
  {#each ["current", "available"] as type, index}
    <div class="all">
      <div class="mb-2" style="height:75px">
        <Flex class="mt-4 align-items-center">
          <FlexItem class="mr-6">
            <h4 class="gl-title level-4" style="width: max-content;">{$_(`${resource}.${type}`)}</h4>
          </FlexItem>
          <FlexItem class="flex-grow-1">
            <TextField variant="Search" bind:value={search[type]} on:input={() => handleSearch(type)} />
          </FlexItem>
        </Flex>
      </div>
      <div class="assignment-container mt-2">
        {#if searched[type]?.length || (!searched[type] && data[type].length)}
          {#each searched[type] || data[type] as el}
            <button
              class={selected[type].includes(el) ? "assignment-item-selected" : "assignment-item"}
              class:newly-added={!initialData[type].includes(el)}
              on:click={() => !disabled && handleAssignmentClick(type, el)}
              tabindex="0"
            >
              <div>
                {#if el?.type === "user"}
                  <Icon icon={faUser} />
                {:else if el?.type === "group"}
                  <Icon icon={faUsers} />
                {/if}

                <span>
                  {el.label}
                </span>
              </div>

              <div class="assignment-item-info">
                {el.info}
              </div>
            </button>
          {/each}
        {:else}
          <div class="assignment-item">{$_("common.no.display")}</div>
        {/if}
      </div>
    </div>
    {#if index === 0}
      <div class="buttons">
        <Flex class="flex-direction-column mt-3">
          <Button
            outlined
            class="mb-2"
            on:click={() => handleAddRemove("add")}
            disabled={disabled || selected.available.length === 0}
          >
            <Icon icon={faArrowLeft} scale={0.9} />
            <span style="font-size:14px">{$_("buttons.add")}</span>
          </Button>
          <Button
            outlined
            on:click={() => handleAddRemove("remove")}
            disabled={disabled || selected.current.length === 0}
          >
            <span style="font-size:14px">{$_("buttons.remove")}</span>
            <Icon icon={faArrowRight} scale={0.9} />
          </Button>
        </Flex>
      </div>
    {/if}
  {/each}
</div>

<style>
  .grid-container {
    display: grid;
    grid-template-columns: 43% 10% 43%;
    grid-column-gap: 2%;
  }

  @media (max-width: 1500px) {
    .grid-container {
      grid-template-columns: 100%;
    }
  }

  .assignment-container {
    border: 1px solid #d9d9d9;
    box-shadow: 0px 8px 16px -2px #0000001a;
    box-shadow: 0px 0px 0px 1px #00000005;
    border-radius: 8px;
    width: 100%;
    background-color: var(--gl-background);
    height: fit-content;
    max-height: 400px;
    overflow-y: scroll;
  }

  .assignment-item,
  .assignment-item-selected {
    min-height: 52px;
    width: 100%;
    border-style: solid;
    border-color: var(--gl-border);
    border-width: 0px 0px 1px 0px;
    background-color: var(--gl-background);
    text-align: left;
    padding: 8px 16px 8px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 16px;
    color: var(--gl-text);
  }

  .assignment-item :global(.gl-icon),
  .assignment-item-selected :global(.gl-icon) {
    color: var(--gl-text-light);
    margin-right: 10px;
  }

  .assignment-item-selected :global(.gl-icon) {
    color: var(--gl-text-light);
    margin-right: 10px;
  }

  .assignment-item:hover {
    color: var(--gl-primary);
    background-color: hsl(0, 0%, 95%);
    cursor: pointer;
  }

  .assignment-item:hover > * {
    color: var(--gl-primary);
  }

  .assignment-item:focus {
    border-width: 1px 1px 1px 1px;
  }

  .assignment-item-selected {
    background: var(--gl-dark);
    color: var(--gl-text-invert);
  }

  .assignment-item-info {
    color: var(--gl-text-light);
    justify-self: end;
    max-width: 50%;
    text-align: end;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .assignment-item-selected > .assignment-item-info {
    color: var(--gl-text-invert);
  }

  .assignment-container :first-child {
    border-radius: 8px 8px 0px 0px;
  }

  .assignment-container :only-child {
    border-radius: 8px 8px 8px 8px;
  }

  .assignment-container :last-child {
    border-radius: 0px 0px 8px 8px;
  }

  @media (min-width: 1500px) {
    .buttons {
      margin-top: 90px;
    }
  }

  .newly-added {
    background-color: #e6f1ff;
  }
</style>
